import api from "@/services/api";
import { useAuthStore } from "@/store/auth";
import { MaybeRefOrGetter, computed, reactive, toRef, toValue } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

export enum Level {
  INFO = "INFO",
  WARNING = "WARNING",
  ERROR = "ERROR",
}

export enum NoticeKind {
  SYSTEM = "SYSTEM",
  ANNOUNCEMENT = "ANNOUNCEMENT",
}

export interface Notice {
  pk: number;
  start: string;
  end: string;
  kind: NoticeKind;
  level: Level;
  title: string;
  content: string;
  imageUrl: string;
  linkUrl: string;
}

export const LIST_NOTICES_URL = "/notices/";

function listNotices(): Promise<Notice[]> {
  return api
    .get(LIST_NOTICES_URL, { params: { kind: NoticeKind.SYSTEM } })
    .then((response) => response.data);
}

export function useNoticesQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["notices"],
      queryFn: listNotices,
      enabled: toRef(authStore, "isAuthed"),
    }),
  );
}

export function useAnnouncementsQuery(
  userId: MaybeRefOrGetter<number | undefined>,
) {
  return reactive(
    useQuery({
      queryKey: ["notices", NoticeKind.ANNOUNCEMENT, userId],
      queryFn: ({ signal }) =>
        api
          .get<Notice[]>(LIST_NOTICES_URL, {
            params: { kind: NoticeKind.ANNOUNCEMENT },
            signal,
          })
          .then((response) => response.data),
      enabled: computed(() => toValue(userId) !== undefined),
    }),
  );
}

export function useDismissAnnouncementMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: ({ noticeId }: { noticeId: number; userId: number }) =>
        api.post(`/notices/${noticeId}/dismiss/`),
      onSuccess: async (_response, variables) => {
        const queryKey = ["notices", NoticeKind.ANNOUNCEMENT, variables.userId];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData<Notice[]>(queryKey, (notices) =>
          notices?.filter((notice) => notice.pk !== variables.noticeId),
        );
        await queryClient.invalidateQueries({ queryKey });
      },
    }),
  );
}
